<div class="auth-wrapper">
  <mat-card class="auth-card" appearance="outlined">
    <mat-card-content class="auth-content">
      @switch (state) {
        @case ('loading') {
          <h1 class="headline">{{ 'mfa.enroll.headline' | transloco }}</h1>
          <div class="mfa-loading" role="status" aria-live="polite">
            <mat-spinner diameter="40"></mat-spinner>
          </div>
        }

        @case ('redirecting') {
          <h1 class="headline">{{ 'mfa.enroll.headline' | transloco }}</h1>
          <div class="mfa-loading" role="status" aria-live="polite">
            <mat-spinner diameter="40"></mat-spinner>
            <p class="supporting">{{ 'mfa.common.redirecting' | transloco }}</p>
          </div>
        }

        @case ('prove') {
          <h1 class="headline">{{ 'mfa.enroll.proveHeadline' | transloco }}</h1>
          @if (!useRecovery) {
            <p class="supporting">{{ (isProveEmail ? 'mfa.enroll.proveSupportingEmail' : 'mfa.enroll.proveSupportingTotp') | transloco }}</p>
            @if (notice) {
              <p class="notice" role="status" data-testid="notice">{{ notice | transloco }}</p>
            }
            <form (submit)="onSubmit($event)" novalidate>
              <app-code-entry
                [length]="codeLength"
                mode="numeric"
                [autofocus]="true"
                [disabled]="submitting"
                [ariaLabel]="'mfa.common.codeLabel' | transloco"
                [cellLabel]="'mfa.common.charLabel' | transloco: {n: '{n}', total: '{total}'}"
                (valueChange)="onCodeChange($event)"
                (completed)="onCodeCompleted($event)"
              />
              @if (error) {
                <p class="error" role="alert" data-testid="error">{{ error | transloco }}</p>
              }
              <button mat-flat-button color="primary" type="submit" data-testid="prove-submit" [disabled]="!canProve">
                {{ (submitting ? 'mfa.challenge.verifying' : 'mfa.challenge.verify') | transloco }}
              </button>
            </form>
            <div class="actions">
              @if (isProveEmail) {
                <button type="button" mat-button class="secondary-action" data-testid="resend"
                        (click)="proveResend()" [disabled]="resendIn > 0 || submitting || resending">
                  {{ resendIn > 0 ? ('mfa.common.resendIn' | transloco: {seconds: resendIn}) : ('mfa.common.resend' | transloco) }}
                </button>
              }
              <button type="button" mat-button class="secondary-action" data-testid="use-recovery" (click)="toggleRecovery()">
                {{ 'mfa.challenge.useRecovery' | transloco }}
              </button>
            </div>
          } @else {
            <p class="supporting">{{ 'mfa.challenge.recoverySupporting' | transloco }}</p>
            <form (submit)="onSubmit($event)" novalidate>
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-label>{{ 'mfa.challenge.recoveryLabel' | transloco }}</mat-label>
                <input matInput [(ngModel)]="recoveryCode" name="recoveryCode" autocomplete="off"
                       spellcheck="false" placeholder="xxxxx-xxxxx" maxlength="32" data-testid="recovery-input" />
              </mat-form-field>
              @if (error) {
                <p class="error" role="alert" data-testid="error">{{ error | transloco }}</p>
              }
              <button mat-flat-button color="primary" type="submit" [disabled]="!canProve">
                {{ (submitting ? 'mfa.challenge.verifying' : 'mfa.challenge.useRecoverySubmit') | transloco }}
              </button>
            </form>
            <div class="actions">
              <button type="button" mat-button class="secondary-action" (click)="toggleRecovery()">
                {{ 'mfa.challenge.backToCode' | transloco }}
              </button>
            </div>
          }
          <div class="actions">
            <button type="button" mat-button class="secondary-action" (click)="backToSignIn()">
              {{ 'mfa.common.backToSignIn' | transloco }}
            </button>
          </div>
        }

        @case ('choose') {
          <h1 class="headline">{{ 'mfa.enroll.headline' | transloco }}</h1>
          <p class="supporting">{{ (held.length ? 'mfa.enroll.supportingAdd' : 'mfa.enroll.supporting') | transloco }}</p>
          <div class="method-options">
            @if (canChoose('totp')) {
              <button type="button" class="method-option" data-testid="choose-totp" (click)="choose('totp')" [disabled]="starting">
                <strong>{{ 'mfa.enroll.totpOption' | transloco }}</strong>
                <span>{{ 'mfa.enroll.totpHint' | transloco }}</span>
              </button>
            }
            @if (canChoose('email')) {
              <button type="button" class="method-option" data-testid="choose-email" (click)="choose('email')" [disabled]="starting">
                <strong>{{ 'mfa.enroll.emailOption' | transloco }}</strong>
                <span>{{ 'mfa.enroll.emailHint' | transloco }}</span>
              </button>
            }
          </div>
          @if (error) {
            <p class="error" role="alert" data-testid="error">{{ error | transloco }}</p>
          }
          <div class="actions">
            <button type="button" mat-button class="secondary-action" (click)="backToSignIn()">
              {{ 'mfa.common.backToSignIn' | transloco }}
            </button>
          </div>
        }

        @case ('confirm') {
          <h1 class="headline">{{ (isEmail ? 'mfa.enroll.confirmEmailHeadline' : 'mfa.enroll.confirmTotpHeadline') | transloco }}</h1>
          @if (isEmail) {
            <p class="supporting">{{ 'mfa.enroll.confirmEmailSupporting' | transloco }}</p>
            @if (notice) {
              <p class="notice" role="status" data-testid="notice">{{ notice | transloco }}</p>
            }
          } @else {
            <p class="supporting">{{ 'mfa.enroll.scan' | transloco }}</p>
            @if (qrDataUrl) {
              <img class="qr" [src]="qrDataUrl" [alt]="'mfa.enroll.qrAlt' | transloco" width="196" height="196" data-testid="qr" />
            }
            <p class="manual-key">
              {{ 'mfa.enroll.manualKey' | transloco }}
              <code class="secret" data-testid="manual-secret">{{ secretGroups }}</code>
            </p>
          }
          <form (submit)="onSubmit($event)" novalidate>
            <app-code-entry
              [length]="codeLength"
              mode="numeric"
              [autofocus]="true"
              [disabled]="submitting"
              [ariaLabel]="'mfa.common.codeLabel' | transloco"
              [cellLabel]="'mfa.common.charLabel' | transloco: {n: '{n}', total: '{total}'}"
              (valueChange)="onCodeChange($event)"
              (completed)="onCodeCompleted($event)"
            />
            @if (error) {
              <p class="error" role="alert" data-testid="error">{{ error | transloco }}</p>
            }
            <button mat-flat-button color="primary" type="submit" [disabled]="!canConfirm">
              {{ (submitting ? 'mfa.enroll.confirming' : 'mfa.enroll.confirm') | transloco }}
            </button>
          </form>
          <div class="actions">
            @if (isEmail) {
              <button type="button" mat-button class="secondary-action" data-testid="resend"
                      (click)="resend()" [disabled]="resendIn > 0 || submitting || resending">
                {{ resendIn > 0 ? ('mfa.common.resendIn' | transloco: {seconds: resendIn}) : ('mfa.common.resend' | transloco) }}
              </button>
            }
            <button type="button" mat-button class="secondary-action" data-testid="choose-another" (click)="chooseAnother()">
              {{ 'mfa.enroll.chooseAnother' | transloco }}
            </button>
          </div>
        }

        @case ('codes') {
          <h1 class="headline">{{ 'mfa.enroll.codesHeadline' | transloco }}</h1>
          <p class="supporting">{{ 'mfa.enroll.codesSupporting' | transloco }}</p>
          <ul class="recovery-codes" data-testid="recovery-codes">
            @for (code of recoveryCodes; track code) {
              <li>{{ code }}</li>
            }
          </ul>
          <!-- Copy and download were added by the 2026-09-05 code review: the
               set is displayed exactly once and only bcrypt hashes are stored,
               so a refresh, a back button or a tab crash used to lose them
               permanently, leaving an operator reset as the only escape hatch. -->
          <div class="actions">
            <button mat-stroked-button type="button" data-testid="codes-copy" (click)="copyCodes()">
              {{ (codesCopied ? 'mfa.enroll.codesCopied' : 'mfa.enroll.codesCopy') | transloco }}
            </button>
            <button mat-stroked-button type="button" data-testid="codes-download" (click)="downloadCodes()">
              {{ 'mfa.enroll.codesDownload' | transloco }}
            </button>
          </div>
          <button mat-flat-button color="primary" type="button" data-testid="codes-saved" (click)="continue()">
            {{ 'mfa.enroll.codesSaved' | transloco }}
          </button>
        }

        @case ('blocked') {
          <h1 class="headline">{{ 'mfa.enroll.headline' | transloco }}</h1>
          <p class="error" role="alert" data-testid="blocked">{{ 'mfa.enroll.notAllowed' | transloco }}</p>
          <div class="actions">
            <button type="button" mat-button class="secondary-action" (click)="backToSignIn()">
              {{ 'mfa.common.backToSignIn' | transloco }}
            </button>
          </div>
        }

        @case ('error') {
          <h1 class="headline">{{ 'mfa.enroll.headline' | transloco }}</h1>
          <p class="error" role="alert">{{ 'mfa.common.loadError' | transloco }}</p>
          <div class="actions">
            <button type="button" mat-button class="secondary-action" (click)="backToSignIn()">
              {{ 'mfa.common.backToSignIn' | transloco }}
            </button>
          </div>
        }
      }
    </mat-card-content>
  </mat-card>
</div>
